/**
 * A RONDA DA MANHÃ — a primeira tela do tratador, e a mais usada do sistema.
 *
 * Ele pega o oxímetro, percorre os tanques na ordem em que anda, e em cada um
 * anota temperatura e oxigênio. Hoje isso vai para um caderno; aqui vai para o
 * banco, e o app responde na hora o que aquela água permite.
 *
 * TRÊS DECISÕES QUE VALE ENTENDER:
 *
 * 1. A ORDEM É A DA RONDA, não a do código. Ele anda A-1, T-1 a T-4, R-2, E-1,
 *    T-5 a T-8. Por código o E-1 e o R-2 subiriam para o começo da lista, e ele
 *    rolaria a tela para cima e para baixo em cada parada.
 *
 * 2. O CÉU SE PERGUNTA UMA VEZ. É o mesmo céu em todos os tanques da mesma
 *    volta. Perguntar por tanque seria onze toques para uma informação só.
 *
 * 3. A RESPOSTA VEM COM O MOTIVO. «50% — quem limitou foi o oxigênio, 2,4» e
 *    não só «50%». Sem o motivo o número é ordem; com ele é explicação, e o
 *    tratador pode discordar sabendo de quê.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  listarRonda, parametrosDoCalculo, registrarLeitura,
  type AnaliseDaAgua, type CeuDoDia, type LeituraDeAgua, type ParametrosDoCalculo,
  type PontoDaRonda,
} from '../acesso/api'
import { descartar, leiturasPendentesDe } from '../acesso/fila'
import {
  tendenciaDoOxigenio, tendenciaEmPalavras, vereditoDaAgua, type Veredito,
} from '../dominio/agua'
import { numero, numeroNaFaixa, soNumero } from '../dominio/unidades'
import { aguaDaConta, juntarComFila, separarFamilias, type MedidaUsada } from '../dominio/agua'
import { GraficoDeOxigenio } from './GraficoDeOxigenio'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'
import { Agrupamento, VoltarAoTopo } from './Agrupamento'

/**
 * Junta o que o servidor sabe com o que ainda está na fila do aparelho.
 *
 * Sem isto o tratador gravaria a leitura, veria o tanque continuar dizendo
 * «não lido» — porque o servidor de fato não sabe — e leria de novo. A fila
 * não é um detalhe de transporte: enquanto ela não esvazia, ela É parte do que
 * a tela tem de mostrar.
 */
export function comPendentes(ponto: PontoDaRonda, dia: string): PontoDaRonda {
  const fila = leiturasPendentesDe(ponto.tanque_id, dia)
  if (fila.length === 0) return ponto
  // AS DUAS FAMÍLIAS SE SEPARAM AQUI TAMBÉM, como na consulta do servidor —
  // e a separação mora no domínio, com teste (`separarFamilias`).
  const familias = separarFamilias(fila)
  const novas: LeituraDeAgua[] = familias.leituras.map((l) => ({
    data: dia,
    hora: l.hora,
    temperatura_c: l.temperatura_c,
    oxigenio_mgl: l.oxigenio_mgl,
    ceu: (l.ceu as LeituraDeAgua['ceu']) ?? null,
    pendente: true,
    id: l.id,
  }))
  const analises: AnaliseDaAgua[] = familias.analises.map((a) => ({
    data: dia,
    hora: a.hora,
    ph: a.ph,
    alcalinidade_mgl: a.alcalinidade_mgl,
    pendente: true,
    id: a.id,
  }))
  // A ORDEM É A DA CONTA, e mora no domínio, com teste (`juntarComFila`).
  // O Trato usa esta mesma função: antes ele não via a leitura ainda na fila.
  const todas = juntarComFila(ponto.leituras, novas)
  const quimica = juntarComFila(ponto.quimica, analises)
  // TRINTA, como o servidor manda. Cortar em seis encolhia o gráfico de
  // oxigênio para três ou quatro dias enquanto houvesse leitura na fila, e
  // ele voltava aos catorze só depois de sincronizar.
  return { ...ponto, leituras: todas.slice(0, 30), quimica: quimica.slice(0, 10) }
}

const CEUS: readonly { valor: CeuDoDia; rotulo: string }[] = [
  { valor: 'sol', rotulo: '☀ sol' },
  { valor: 'parcial', rotulo: '⛅ parcial' },
  { valor: 'nublado', rotulo: '☁ nublado' },
  { valor: 'chuva', rotulo: '🌧 chuva' },
]

export function Ronda() {
  const [dia, setDia] = useState(hojeISO())
  const [pontos, setPontos] = useState<PontoDaRonda[]>([])
  const [calculo, setCalculo] = useState<ParametrosDoCalculo | null>(null)
  const [ceu, setCeu] = useState<CeuDoDia | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  // Muda a cada gravação na fila, para a tela recontar o que já foi lido sem
  // precisar do servidor.
  const [versao, setVersao] = useState(0)

  const recarregar = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([listarRonda(dia), parametrosDoCalculo()])
      setPontos(r)
      setCalculo(p)
      setErro(null)
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
    } finally {
      setCarregando(false)
    }
  }, [dia])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  if (carregando) return <p className="aviso">Carregando…</p>

  void versao
  const comFila = pontos.map((p) => comPendentes(p, dia))
  const lidosHoje = comFila.filter((p) => p.leituras.some((l) => l.data.slice(0, 10) === dia))

  return (
    <div className="ronda">
      {erro && <p className="aviso-erro">{erro}</p>}

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      <section className="bloco">
        <h2>Ronda</h2>
        <p className="secundario">
          {lidosHoje.length} de {comFila.length} tanques lidos
        </p>

        <div className="ceu">
          <span className="campo-rotulo">Como está o céu agora?</span>
          <div className="ceu-opcoes">
            {CEUS.map((c) => (
              <button
                key={c.valor}
                type="button"
                className={ceu === c.valor ? 'ceu-botao ativo' : 'ceu-botao'}
                onClick={() => setCeu(c.valor)}
              >
                {c.rotulo}
              </button>
            ))}
          </div>
          <span className="secundario">
            Vale para todos os tanques desta volta — pergunta uma vez só.
          </span>
        </div>
      </section>

      {comFila.length === 0 ? (
        <p className="aviso">
          Nenhum tanque com peixe estocado. Sem ciclo aberto não há o que ler.
        </p>
      ) : (
        <>
          {/* A LEGENDA, dita uma vez, como no Trato. */}
          <p className="legenda-gaveta secundario">temperatura · oxigênio, hoje</p>

          <Agrupamento
            tela="ronda"
            itens={comFila.map((ponto) => ({
              chave: ponto.ciclo_id,
              titulo: ponto.tanque,
              setor: ponto.setor,
              // PENDENTE NA RONDA é tanque sem leitura NO DIA. A pergunta
              // desta tela é «já medi aqui hoje?», e é ela que o contador do
              // setor precisa responder.
              pendente: !leuHoje(ponto, dia),
              ponto,
            }))}
            resumo={(i) => <ResumoDaRonda ponto={i.ponto} dia={dia} />}
            ficha={(i) => (
              <Parada
                ponto={i.ponto}
                calculo={calculo}
                ceu={ceu}
                dia={dia}
                aoGravar={() => setVersao((v) => v + 1)}
              />
            )}
          />
          <VoltarAoTopo />
        </>
      )}
    </div>
  )
}

/** A leitura DESTE dia, se houver. É o que a ronda existe para produzir. */
function leituraDoDia(ponto: PontoDaRonda, dia: string): LeituraDeAgua | undefined {
  return ponto.leituras.find((l) => l.data.slice(0, 10) === dia)
}

function leuHoje(ponto: PontoDaRonda, dia: string): boolean {
  return leituraDoDia(ponto, dia) !== undefined
}

/**
 * A LINHA FECHADA DA RONDA.
 *
 * A pergunta desta tela não é a do Trato. Aqui ninguém quer saber quanto
 * ofereceu — quer saber SE JÁ MEDIU e o que deu. Então a linha traz a leitura
 * do próprio dia, e quando não há, diz que não há em vez de mostrar a de
 * ontem: leitura velha na linha fechada seria a tela respondendo «já medi»
 * quando ninguém mediu.
 *
 * A de ontem continua aparecendo dentro da ficha, onde está rotulada.
 */
function ResumoDaRonda({ ponto, dia }: { ponto: PontoDaRonda; dia: string }) {
  const hoje = leituraDoDia(ponto, dia)
  if (!hoje) return <span className="gaveta-falta">sem leitura</span>

  // CADA MEDIDA PELA SUA LEITURA MAIS RECENTE DO DIA — a mesma conta do Trato.
  // Antes a linha mostrava só a última leitura, e a que completava a medida
  // esquecida escondia a outra.
  const agua = aguaDaConta(ponto.leituras, dia)
  const partes = [
    agua.temperatura === null ? null : `${numero(agua.temperatura.valor, 1)} °C`,
    agua.oxigenio?.doDia ? `${numero(agua.oxigenio.valor, 1)} mg/L` : null,
  ].filter((x) => x !== null)

  return (
    <span className="gaveta-bem">
      ✓ {partes.length > 0 ? partes.join(' · ') : 'lido'}
    </span>
  )
}

function Parada({
  ponto, calculo, ceu, dia, aoGravar,
}: {
  ponto: PontoDaRonda
  calculo: ParametrosDoCalculo | null
  ceu: CeuDoDia | null
  dia: string
  aoGravar: () => void
}) {
  const [oxigenio, setOxigenio] = useState('')
  const [temperatura, setTemperatura] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  // Abre o formulário de novo quando já há leitura no dia.
  const [outra, setOutra] = useState(false)

  const tendencia = tendenciaDoOxigenio(ponto.leituras)
  // A ÚLTIMA DE CADA MEDIDA, cada uma com o seu dia. «última leitura hoje ·
  // 28,5 °C» escondia o oxigênio de vinte minutos antes.
  const ultimaOxi = ponto.leituras.find((l) => l.oxigenio_mgl !== null) ?? null
  const ultimaTemp = ponto.leituras.find((l) => l.temperatura_c !== null) ?? null
  const ultima = ultimaOxi ?? ultimaTemp
  const mesmoDia = !ultimaOxi || !ultimaTemp || ultimaOxi.data.slice(0, 10) === ultimaTemp.data.slice(0, 10)
  // O último dia ANTES do escolhido em que houve leitura — e todas as dele, da
  // primeira para a última.
  const dataAnterior = ponto.leituras.find((l) => l.data.slice(0, 10) < dia)?.data.slice(0, 10)
  const diaAnterior = dataAnterior
    ? ponto.leituras.filter((l) => l.data.slice(0, 10) === dataAnterior).reverse()
    : []

  // O QUE O CAMPO DEVOLVE É TEXTO, e texto pode ser qualquer coisa.
  // `2.9\` vira NaN, e NaN não é maior nem menor que nada — passava por todas
  // as comparações sem cair em faixa nenhuma, e o app dizia «água boa».
  //
  // São duas defesas, e as duas precisam existir: o campo só aceita algarismo,
  // vírgula e ponto (não dá nem para digitar o lixo), e a leitura confere a
  // FAIXA DO POSSÍVEL, que é a mesma do CHECK da tabela. Campo limpo protege
  // contra escorregão; faixa protege contra dedo pesado no zero.
  const nOxi = numeroNaFaixa(oxigenio, 0, 30)
  const nTemp = numeroNaFaixa(temperatura, 0, 50)
  const oxiRuim = oxigenio.trim() !== '' && nOxi === null
  const tempRuim = temperatura.trim() !== '' && nTemp === null

  // O veredito aparece ENQUANTO se digita. É o momento em que o tratador ainda
  // pode conferir se leu o aparelho direito, antes de gravar.
  // A PRÉVIA É A CONTA DO TRATO com esta leitura somada às do dia: quem
  // digita só a temperatura esquecida vê a resposta com o oxigênio que já
  // gravou, e não uma água sem oxigênio.
  // Pela MESMA ordenação da gravação (`juntarComFila`): em dia passado a hora
  // é a de agora, e a leitura pode não ser a mais recente daquele dia.
  const horaDestaLeitura = agora()
  const comEsta = calculo && (nOxi !== null || nTemp !== null)
    ? aguaDaConta(
        juntarComFila(ponto.leituras, [
          { data: dia, hora: horaDestaLeitura, oxigenio_mgl: nOxi, temperatura_c: nTemp, ceu: null },
        ]),
        dia,
      )
    : null
  const previa: Veredito | null =
    calculo && comEsta
      ? vereditoDaAgua(comEsta.oxigenio?.valor ?? null, comEsta.temperatura?.valor ?? null, calculo.coeficientes)
      : null
  // DE QUANDO É O NÚMERO QUE LIMITOU. Pode não ser o digitado: com só a
  // temperatura digitada, quem manda pode ser o oxigênio de mais cedo, ou o
  // de ontem. Dizer «2,4 mg/L agora» de um número que ninguém mediu agora é
  // eco com cara de medição.
  const fonte: MedidaUsada | null =
    previa?.limitou === 'oxigenio' ? (comEsta?.oxigenio ?? null)
      : previa?.limitou === 'temperatura' ? (comEsta?.temperatura ?? null)
        : null
  const digitado = previa?.limitou === 'oxigenio' ? nOxi : nTemp
  const ehEsta =
    fonte !== null && fonte.data === dia && fonte.hora === horaDestaLeitura && fonte.valor === digitado
  const deQuando =
    fonte === null || ehEsta
      ? dia === hojeISO() ? 'agora' : 'nesta leitura'
      : fonte.doDia
        ? fonte.hora ? `das ${fonte.hora.slice(0, 5)}` : 'de mais cedo'
        : `de ${diaCurtoDe(fonte.data)}`

  const doDia = ponto.leituras.filter((l) => l.data.slice(0, 10) === dia)
  const jaLido = doDia.length > 0

  return (
    // SEM ESMAECER O TANQUE LIDO (04/10/2026, pedido do gestor: «contraste
    // como no Trato»). A ficha inteira ia a 55% de opacidade — leituras,
    // gráfico e botões —, e no sol isso apaga justamente o que se quer
    // conferir. Quem diz «já lido» é a linha fechada (✓ em verde) e a
    // etiqueta «lido» aqui dentro, como no Trato.
    <section className="parada">
      <div className="parada-topo">
        <strong>{ponto.tanque}</strong>
        <span className="etiqueta">{ponto.ciclo}</span>
        {ponto.tipo === 'represa' && <span className="etiqueta">represa fonte</span>}
        {jaLido && (
          <span className="etiqueta etiqueta-fria">
            {doDia.length > 1 ? `${doDia.length} leituras` : 'lido'}
            {doDia.some((l) => l.pendente) && ' · na fila'}
          </span>
        )}
      </div>

      {ultima && (
        <div className="secundario">
          última leitura {mesmoDia ? quando(ultima.data) : ''}
          {ultimaOxi && ultimaOxi.oxigenio_mgl !== null && (
            <> · O₂ {numero(ultimaOxi.oxigenio_mgl, 1)}{!mesmoDia && ` (${quando(ultimaOxi.data)})`}</>
          )}
          {ultimaTemp && ultimaTemp.temperatura_c !== null && (
            <> · {numero(ultimaTemp.temperatura_c, 1)} °C{!mesmoDia && ` (${quando(ultimaTemp.data)})`}</>
          )}
          {' · '}
          {tendenciaEmPalavras(tendencia)}
        </div>
      )}

      {/* AS LEITURAS DO ÚLTIMO DIA MEDIDO, TODAS (05/10/2026, pedido do
          gestor). Quando aquele dia teve duas leituras — a da manhã e a
          releitura —, a linha acima mostrava só a mais recente, e a primeira
          sumia de vista. Na ordem em que foram feitas, com a hora. Uma só
          leitura já está dita na linha acima. */}
      {diaAnterior.length > 1 && (
        <div className="leituras-anteriores secundario">
          <span>leituras de {quando(diaAnterior[0]?.data ?? '')}:</span>
          <ul>
            {diaAnterior.map((l, i) => (
              <li key={`${l.hora ?? 'sem-hora'}-${i}`}>
                {l.hora ? l.hora.slice(0, 5) : 'sem hora'} · O₂{' '}
                {l.oxigenio_mgl !== null ? numero(l.oxigenio_mgl, 1) : '—'} ·{' '}
                {l.temperatura_c !== null ? `${numero(l.temperatura_c, 1)} °C` : '—'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* O GRÁFICO MORA COLADO NA FRASE QUE ELE EXPLICA. «oxigênio caindo» e
          o botão de ver de onde isso vem são a mesma pergunta, e separá-los
          faria o segundo virar um enfeite no rodapé.

          Ele some sozinho quando não há série — piscicultura sem oxímetro não
          vê gráfico vazio, que foi a regra dada junto com o pedido. */}
      {calculo && (
        <GraficoDeOxigenio leituras={ponto.leituras} faixas={calculo.coeficientes} />
      )}

      {/* AS LEITURAS DO DIA, TODAS.
          Antes cabia uma só: havendo leitura no dia, o formulário sumia. O
          efeito foi o pior possível para um app que existe para guardar o que
          se observa — a equipe parou de registrar a medição da manhã para
          não «tomar corte», e esperava a tarde para escolher qual registrar.
          Medição feita e não guardada é a única perda que este sistema não
          pode causar.

          A ordem é da mais recente para a mais antiga, que é a mesma ordem em
          que a conta as lê. */}
      {jaLido && (
        <ul className="registrado">
          {doDia.map((l, i) => (
            <li
              key={l.id ?? `${l.hora}-${i}`}
              className={l.pendente ? 'registrado-item na-fila' : 'registrado-item'}
            >
              <span className="registrado-marca">✓</span>
              <span>
                <strong>{l.hora ? l.hora.slice(0, 5) : `leitura ${doDia.length - i}`}</strong>{' '}
                O₂ {l.oxigenio_mgl !== null ? numero(l.oxigenio_mgl, 1) : '—'} ·{' '}
                {l.temperatura_c !== null ? `${numero(l.temperatura_c, 1)} °C` : '—'}
                {/* QUAL LEITURA VALE NA CONTA — por medida: o oxigênio da
                    mais recente que tem oxigênio, a temperatura da mais
                    recente que tem temperatura (`aguaDaConta`). */}
                {doDia.length > 1 && (
                  <QualVale
                    oxigenio={i === doDia.findIndex((x) => x.oxigenio_mgl !== null)}
                    temperatura={i === doDia.findIndex((x) => x.temperatura_c !== null)}
                  />
                )}
                {l.pendente && <span className="secundario"> · na fila</span>}
              </span>
              {/* Desfazer aparece só enquanto a leitura está na fila. Depois de
                  subir, ela é um registro do banco e sair dali é lançamento que
                  anula — que ainda não existe. */}
              {l.pendente && l.id && (
                <button
                  type="button"
                  className="discreto"
                  onClick={() => {
                    descartar(l.id as string)
                    aoGravar()
                  }}
                >
                  {/* Registro da v67 tinha as duas famílias numa linha só:
                      desfazer leva as duas, e o botão tem de dizer. */}
                  {ponto.quimica.some((q) => q.id === l.id) ? 'desfazer (leva o pH junto)' : 'desfazer'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {jaLido && !outra && (
        <>
          <button type="button" className="discreto" onClick={() => setOutra(true)}>
            + outra leitura
          </button>
          {/* A REGRA, dita onde ela importa. Sem esta frase, quem já levou
              corte uma vez calcula que registrar de novo pode piorar — e
              guarda a medição para si, que foi o que aconteceu. */}
          <p className="secundario">
            Mediu de novo, ou esqueceu uma das duas? Registre só o que tiver — a outra
            continua valendo. A conta usa, de cada medida, a leitura mais recente do dia.
          </p>
        </>
      )}

      {(!jaLido || outra) && (
        <form
          className="parada-leitura"
          onSubmit={(evento) => {
            evento.preventDefault()
            setSalvando(true)
            setErro(null)
            try {
              // Grava no APARELHO. Não toca a rede, não falha por falta de
              // sinal, e por isso não existe «tente de novo» aqui.
              registrarLeitura({
                tanque_id: ponto.tanque_id,
                data: dia,
                hora: agora(),
                temperatura_c: nTemp,
                oxigenio_mgl: nOxi,
                ph: null,
                alcalinidade_mgl: null,
                ceu,
                observacao: null,
              }, { rotulo: ponto.tanque })
              // LIMPAR E FECHAR. Antes o formulário desaparecia sozinho porque
              // a tela virava «já lido»; agora ele pode ficar aberto, e deixar
              // os números lá dentro seria convite para registrar de novo a
              // mesma leitura — o erro que o T-8 já cobrou caro no trato.
              setOxigenio('')
              setTemperatura('')
              setOutra(false)
              aoGravar()
            } catch (falha) {
              setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
            } finally {
              setSalvando(false)
            }
          }}
        >
          <div className="parada-campos">
            <label className="campo">
              <span className="campo-rotulo">Oxigênio (mg/L)</span>
              <input
                value={oxigenio}
                onChange={(e) => setOxigenio(soNumero(e.target.value))}
                inputMode="decimal"
                placeholder="3,8"
                className={oxiRuim ? 'campo-invalido' : undefined}
              />
              {oxiRuim && <span className="campo-aviso">entre 0 e 30 mg/L</span>}
            </label>
            <label className="campo">
              <span className="campo-rotulo">Temperatura (°C)</span>
              <input
                value={temperatura}
                onChange={(e) => setTemperatura(soNumero(e.target.value))}
                inputMode="decimal"
                placeholder="28,4"
                className={tempRuim ? 'campo-invalido' : undefined}
              />
              {tempRuim && <span className="campo-aviso">entre 0 e 50 °C</span>}
            </label>
          </div>


          {previa && <Resposta previa={previa} deQuando={deQuando} />}
          {erro && <p className="aviso-erro">{erro}</p>}

          <button
            type="submit"
            className="botao-grande"
            disabled={salvando || previa === null || oxiRuim || tempRuim}
          >
            {salvando ? 'Gravando…' : 'Gravar leitura'}
          </button>
        </form>
      )}

      <AnaliseQuimica ponto={ponto} dia={dia} aoGravar={aoGravar} />
    </section>
  )
}

/**
 * pH E ALCALINIDADE — a análise avulsa, separada da ronda.
 *
 * Correção do gestor em 04/10/2026, no mesmo dia em que os campos entraram
 * presos à leitura de oxigênio: *a ronda matinal é rápida, com aparelho; pH e
 * alcalinidade dependem de estequiometria, análise demorada*. São momentos
 * diferentes, então são registros diferentes — e esta seção grava sozinha,
 * sem oxigênio nem temperatura.
 *
 * O QUE ELA NÃO FAZ, de propósito:
 *   - não marca o tanque como «lido» na ronda — a pergunta da ronda é «já
 *     medi o oxigênio hoje?», e uma análise de pH não responde isso;
 *   - não entra no Trato nem no gráfico — o servidor manda as análises numa
 *     lista própria (`quimica`), e a conta do trato nunca a vê;
 *   - não grava hora: a hora de lançar não é a hora da coleta, e a análise
 *     muitas vezes é lançada horas depois. O DIA é o que vale, e se escolhe
 *     nas setas lá em cima — o dia em que a amostra foi colhida.
 *
 * Fica atrás de um toque porque não é coisa de toda manhã, e campo sempre
 * aberto custa segundos na ronda de todo dia.
 */
function AnaliseQuimica({
  ponto, dia, aoGravar,
}: {
  ponto: PontoDaRonda
  dia: string
  aoGravar: () => void
}) {
  const [aberta, setAberta] = useState(false)
  const [ph, setPh] = useState('')
  const [alcalinidade, setAlcalinidade] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  const nPh = numeroNaFaixa(ph, 1, 14)
  const nAlc = numeroNaFaixa(alcalinidade, 0, 1000)
  const phRuim = ph.trim() !== '' && nPh === null
  const alcRuim = alcalinidade.trim() !== '' && nAlc === null
  // O MEDIDOR DE pH MOSTRA PONTO no visor (7.20), e quem copia do visor
  // copia o ponto. O padrão do app recusa — ponto é milhar —, então o aviso
  // tem de dizer o porquê, e não «entre 1 e 14» para um 7,2.
  const comPonto = (t: string) => t.includes('.') && !t.includes(',')

  const doDia = ponto.quimica.filter((a) => a.data.slice(0, 10) === dia)
  const ultima = ponto.quimica.find((a) => a.data.slice(0, 10) !== dia) ?? null
  const podeGravar = (nPh !== null || nAlc !== null) && !phRuim && !alcRuim

  function gravar() {
    if (!podeGravar) return
    setErro(null)
    try {
      registrarLeitura({
        tanque_id: ponto.tanque_id,
        data: dia,
        hora: null,
        temperatura_c: null,
        oxigenio_mgl: null,
        ph: nPh,
        alcalinidade_mgl: nAlc,
        ceu: null,
        observacao: null,
      }, { rotulo: ponto.tanque })
      setPh('')
      setAlcalinidade('')
      setAberta(false)
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  return (
    <div className="analise-quimica">
      {doDia.length > 0 && (
        <ul className="registrado">
          {doDia.map((a, i) => (
            <li
              key={a.id ?? `analise-${i}`}
              className={a.pendente ? 'registrado-item na-fila' : 'registrado-item'}
            >
              <span className="registrado-marca">✓</span>
              <span>
                <strong>análise</strong>
                {a.ph !== null && <> · pH {numero(a.ph, 1)}</>}
                {a.alcalinidade_mgl !== null && <> · alcalinidade {numero(a.alcalinidade_mgl, 0)} mg/L</>}
                {a.pendente && <span className="secundario"> · na fila</span>}
              </span>
              {a.pendente && a.id && (
                <button
                  type="button"
                  className="discreto"
                  onClick={() => {
                    descartar(a.id as string)
                    aoGravar()
                  }}
                >
                  {ponto.leituras.some((l) => l.id === a.id) ? 'desfazer (leva o oxigênio junto)' : 'desfazer'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!aberta ? (
        <div className="analise-topo">
          <button type="button" className="discreto" onClick={() => setAberta(true)}>
            + pH e alcalinidade
          </button>
          {ultima && (
            <span className="secundario">
              última análise {quando(ultima.data)}
              {ultima.ph !== null && <> · pH {numero(ultima.ph, 1)}</>}
              {ultima.alcalinidade_mgl !== null && <> · {numero(ultima.alcalinidade_mgl, 0)} mg/L</>}
            </span>
          )}
        </div>
      ) : (
        <>
          <p className="secundario">
            Análise do dia {diaCurtoDe(dia)} — se a amostra é de outro dia, troque nas setas
            lá em cima. Pode lançar só um dos dois.
          </p>
          <div className="parada-campos">
            <label className="campo">
              <span className="campo-rotulo">pH</span>
              <input
                value={ph}
                onChange={(e) => setPh(soNumero(e.target.value))}
                inputMode="decimal"
                placeholder="7,2"
                className={phRuim ? 'campo-invalido' : undefined}
              />
              {phRuim && (
                <span className="campo-aviso">
                  {comPonto(ph) ? 'use vírgula: 7,2' : 'entre 1 e 14'}
                </span>
              )}
            </label>
            <label className="campo">
              <span className="campo-rotulo">Alcalinidade (mg/L)</span>
              <input
                value={alcalinidade}
                onChange={(e) => setAlcalinidade(soNumero(e.target.value))}
                inputMode="decimal"
                placeholder="40"
                className={alcRuim ? 'campo-invalido' : undefined}
              />
              {alcRuim && (
                <span className="campo-aviso">
                  {comPonto(alcalinidade) ? 'use vírgula para o decimal' : 'entre 0 e 1.000 mg/L'}
                </span>
              )}
            </label>
          </div>
          {erro && <p className="aviso-erro">{erro}</p>}
          <div className="analise-acoes">
            <button type="button" className="botao-grande" disabled={!podeGravar} onClick={gravar}>
              Gravar análise
            </button>
            <button
              type="button"
              className="discreto"
              onClick={() => {
                setPh('')
                setAlcalinidade('')
                setAberta(false)
              }}
            >
              cancelar
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function diaCurtoDe(iso: string): string {
  const [ano, mes, d] = iso.split('-')
  return ano && mes && d ? `${d}/${mes}` : iso
}

/**
 * QUAL LEITURA DO DIA VALE NA CONTA — por medida.
 *
 * Com a medida esquecida completada depois, o oxigênio pode vir de uma
 * leitura e a temperatura de outra. A etiqueta diz qual, em vez de marcar uma
 * linha só como «a que vale» e deixar a outra parecer descartada.
 */
function QualVale({ oxigenio, temperatura }: { oxigenio: boolean; temperatura: boolean }) {
  if (oxigenio && temperatura) return <span className="secundario"> · é esta que vale na conta</span>
  if (oxigenio) return <span className="secundario"> · o oxigênio desta vale na conta</span>
  if (temperatura) return <span className="secundario"> · a temperatura desta vale na conta</span>
  return null
}

/**
 * O que aquela água permite, em uma frase.
 *
 * `adiar` merece texto próprio: água fria não é motivo para oferecer menos, é
 * motivo para voltar mais tarde. E desde a migração 009 vale também para o
 * OXIGÊNIO, que sobe ao longo da manhã pela fotossíntese das algas — é por
 * isso que o tratador já empurra a ração para a tarde nos dias ruins, e as
 * fichas de setembro mostram isso dia a dia.
 *
 * Dizer «50%» num dia frio de manhã faria o
 * tratador cortar ração que o peixe comeria às dez horas.
 */
function Resposta({ previa, deQuando }: { previa: Veredito; deQuando: string }) {
  if (previa.limitou === null) {
    return <p className="resposta boa">Água boa — trato cheio.</p>
  }

  const quem = previa.limitou === 'oxigenio' ? 'o oxigênio' : 'a temperatura'
  const valor =
    previa.valor === null
      ? ''
      : previa.limitou === 'oxigenio'
        ? `${numero(previa.valor, 1)} mg/L`
        : `${numero(previa.valor, 1)} °C`

  if (previa.acao === 'adiar') {
    // O motivo de esperar é diferente em cada fator, e dizer o motivo certo é
    // o que faz o tratador entender a regra em vez de decorá-la.
    const porque =
      previa.limitou === 'oxigenio'
        ? 'o oxigênio sobe ao longo da manhã, com o sol nas algas'
        : 'a água esquenta com o sol'
    return (
      <p className="resposta adiar">
        <strong>Melhor à tarde.</strong> {valor} {deQuando} — {porque}.
        {previa.coeficiente < 1 && (
          <> Se tratar assim mesmo, {Math.round(previa.coeficiente * 100)}% do previsto.</>
        )}
      </p>
    )
  }

  if (previa.coeficiente === 0) {
    return (
      <p className="resposta ruim">
        <strong>Não tratar hoje.</strong> Quem impediu foi {quem}, {valor}
        {deQuando === 'agora' ? '' : ` ${deQuando}`}.
      </p>
    )
  }

  return (
    <p className="resposta parcial">
      <strong>{Math.round(previa.coeficiente * 100)}% do previsto.</strong> Quem
      limitou foi {quem}, {valor}{deQuando === 'agora' ? '' : ` ${deQuando}`}.
      <br />
      <span className="secundario">Voltar por volta das 10h pode liberar mais.</span>
    </p>
  )
}

function agora(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function quando(iso: string): string {
  const data = new Date(iso)
  const dias = Math.floor((Date.now() - data.getTime()) / 86_400_000)
  if (dias <= 0) return 'hoje'
  if (dias === 1) return 'ontem'
  if (dias < 7) return `há ${dias} dias`
  return data.toLocaleDateString('pt-BR')
}
